import React from 'react';
import { Shield, Download, Eye, ArrowRight } from 'lucide-react';

const DOCS = {
  pdf: 'https://customer-assets.emergentagent.com/job_c2bb8c09-4588-4153-b825-a008e90605ce/artifacts/lbhgmyyl_Forsyt_Security_Onboarding_Final_.pdf',
  nda: 'https://customer-assets.emergentagent.com/job_c2bb8c09-4588-4153-b825-a008e90605ce/artifacts/02jhxa1l_Forsyt-NDA.docx',
};

const PILLARS = [
  {
    title: 'Zero-Data-Retention',
    desc: 'Prompts and responses are processed in-memory. Nothing persisted on Forsyt servers.',
    accent: 'bg-blue-500',
  },
  {
    title: 'Legally Bound via NDA',
    desc: 'Every engagement begins with a mutual NDA. Your pipeline data stays confidential by contract.',
    accent: 'bg-emerald-500',
  },
  {
    title: 'Deploy on Your Terms',
    desc: 'Hosted in our managed AWS cloud (EU / India / US regions). Private VPC and on‑prem deployments available on request for Enterprise contracts.',
    accent: 'bg-purple-500',
  },
  {
    title: 'Partner-Led Setup',
    desc: 'A named technical contact from Forsyt owns every rollout. No self-serve surprises, no hidden data flows.',
    accent: 'bg-amber-500',
  },
];

const SecuritySection = () => (
  <section id="security" className="relative py-24 bg-gray-50 overflow-hidden" data-testid="security-section">
    {/* subtle decorative grid */}
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.035]"
      style={{
        backgroundImage:
          'linear-gradient(to right, #0f172a 1px, transparent 1px), linear-gradient(to bottom, #0f172a 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }}
    />
    <div className="relative max-w-6xl mx-auto px-6">
      {/* Header */}
      <div className="text-center mb-14">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5">
          <Shield className="w-3.5 h-3.5 text-blue-600" />
          <span className="text-xs font-bold text-blue-700 uppercase tracking-widest">Enterprise-grade trust</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          Security and Data Privacy, <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">by design</span>
        </h2>
        <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Revenue data is the most sensitive asset your company owns. Forsyt is engineered so it
          never leaves your control, by architecture and by contract.
        </p>
      </div>

      {/* Trust pillars grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {PILLARS.map((p, i) => (
          <div
            key={p.title}
            className="group relative bg-white rounded-2xl p-6 border border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
            data-testid={`security-pillar-${i}`}
          >
            {/* left accent bar */}
            <div className={`absolute top-6 bottom-6 left-0 w-1 rounded-r ${p.accent} opacity-70`} />
            <h3 className="text-base font-bold text-gray-900 mb-2 pl-3">{p.title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed pl-3">{p.desc}</p>
          </div>
        ))}
      </div>

      {/* Compliance strip, only truthful claims */}
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-10 text-xs font-semibold uppercase tracking-widest text-gray-500">
        <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> AES-256 IN TRANSIT &amp; AT REST</span>
        <span className="hidden sm:block w-px h-4 bg-gray-200" />
        <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> GDPR COMPLIANT</span>
        <span className="hidden sm:block w-px h-4 bg-gray-200" />
        <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> ROLE-BASED ACCESS</span>
        <span className="hidden sm:block w-px h-4 bg-gray-200" />
        <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> MUTUAL NDA REQUIRED</span>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          onClick={() => window.open(DOCS.pdf, '_blank')}
          className="group inline-flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full hover:border-blue-300 hover:shadow-md transition-all text-sm font-semibold text-gray-900 cursor-pointer"
          data-testid="security-overview-link"
        >
          <Eye className="w-4 h-4 text-red-500" /> Security Overview
          <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:translate-x-0.5 group-hover:text-blue-600 transition-all" />
        </button>
        <button
          onClick={() => { const a = document.createElement('a'); a.href = DOCS.nda; a.download = 'Forsyt-NDA.docx'; a.click(); }}
          className="group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full hover:shadow-xl transition-all text-sm font-semibold cursor-pointer"
          data-testid="nda-download-link"
        >
          <Download className="w-4 h-4" /> Download Mutual NDA
          <ArrowRight className="w-3.5 h-3.5 opacity-80 group-hover:translate-x-0.5 transition-all" />
        </button>
      </div>
    </div>
  </section>
);

export default SecuritySection;
